import { useForm } from "react-hook-form";
import Input from "@/components/atoms/Input";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { router } from "@inertiajs/react";
import { ColumnDef } from "@tanstack/react-table";
import TabTable, { SetData, SetOpen } from "../../Account/Tabs/TabTable";
import type FormFieldItem from "@/types/Form/FormFieldItem";
import Button from "@/components/atoms/Button";
import { Plus, SquarePen } from "lucide-react";
import { useEffect, useState } from "react";
import TabSheet from "../../Account/Tabs/TabSheet";
import useAccess from "@/hooks/useAccess";
import stringSchema from "@/lib/utils/stringSchema";
import Priority from "@/types/Priority";
import ColumnHeader from "@/components/atoms/ColumnHeader";
import { capitalize } from "@/lib/utils";
import Pagination from "@/components/atoms/Pagination";

const formSchema = z.object({
  name: stringSchema({ key: "Name", required: true })
});

type Props = {
  data: Priority[];
  redirect_props?: any;
};

const PriorityTab = ({ data, redirect_props }: Props) => {
  const { getAccess } = useAccess();
  const [edit, setEdit] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, per_page: 10 });
  const totalCount = data.length;
  const totalPages = Math.ceil(totalCount / pagination.per_page);
  const defaultValues = {
    name: ""
  };

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const getColumns = (
    setOpen: SetOpen,
    setData: SetData
  ): ColumnDef<Priority>[] => [
    {
      accessorKey: "id",
      header: ({ column }) => <ColumnHeader column={column}>ID</ColumnHeader>,
    },
    {
      accessorKey: "name",
      header: ({ column }) => <ColumnHeader column={column}>Name</ColumnHeader>,
    }
  ];

  const onSubmit = (
    mode: "edit" | "create",
    data: any,
    sheetData: Priority | null
  ) => {
    const values = form.getValues();

    const payload = {
      priority: {
        name: values.name || ""
      },
    };

    if (mode === "edit") {
      router.put(`/priorities/${sheetData?.id}`, payload, {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      });
    } else if (mode === "create") {
      router.post(`/priorities`, payload, {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      });
    }
  };

  const formFields: FormFieldItem<Priority>[] = [
    {
      label: "Name",
      key: "name",
      required: true,
      render: (props) => (
        <Input
          placeholder="Name"
          danger={
            !!form.formState.errors[
              props.name as keyof typeof form.formState.errors
            ]
          }
          disabled={!edit && !createOpen}
          className=""
          {...props}
        />
      ),
    }
  ];

  const handlePageChange = (page: number) => { 
    setPagination(prev => ({ ...prev, page }));
  };

  useEffect(() => {
    if (createOpen) form.reset(defaultValues);
  }, [createOpen]);

  useEffect(() => {
    if (redirect_props && redirect_props.errors) {
      const errors = redirect_props.errors;
      Object.entries(errors).forEach(([key, messages]) => {
        if (Array.isArray(messages)) {
          form.setError(key as keyof typeof form.getValues, {
            type: "manual",
            message: `${capitalize(key)} ${messages[0]}`,
          });
        }
      });
    } else {
      setCreateOpen(false);
      setEdit(false);
    }
  }, [redirect_props]);

  return (
    <div>

      <Button
        className="float-right m-4"
        disabled={!getAccess(["create_priority"])}
        onClick={() => {
          setCreateOpen(true);
        }}
      >
        <Plus size={16} className="mr-2" />
        Add New Priority
      </Button>

      <TabTable
        columns={getColumns}
        data={data.map((d) => ({
          ...d
        }))}
        form={form}
        formFields={formFields}
        onSubmit={onSubmit}
        edit={edit}
        setEdit={setEdit}
        currentPage={pagination.page}
        pageSize={pagination.per_page}
        loading={loading}
      />

      <Pagination
        currentPage={pagination.page}
        totalPages={totalPages}
        totalCount={totalCount}
        perPage={pagination.per_page}
        onPageChange={handlePageChange}
      />

      <TabSheet
        title="Create Priority"
        form={form}
        mode="create"
        data={null}
        open={createOpen}
        formFields={formFields}
        onOpen={setCreateOpen}
        onSubmit={onSubmit}
      />
    </div>
  );
};

export default PriorityTab;
